/** @jsxImportSource @emotion/react */

import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { createSearchParams, useParams, useSearchParams } from 'react-router-dom'
import { useMemo } from 'react'
import { Typography } from '@mui/material'
import { last } from 'lodash'
import { BLOG_POST_MEDIA_PATH_S3 } from 'constants/images'
import { getTableOfContent, validateTableOfContent } from 'components/TableOfContent'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { useImageUpload } from 'utils/saveImageS3'
import { PageLoader } from 'components/PageLoader'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { ImageField } from 'components/DropzoneField'
import * as classes from 'pagesNew/SuccessStory/Create/styles'
import { FormValues } from 'pagesNew/SuccessStory/Create/types'
import { BlogPostType, Form, VALIDATE_TABLE_OF_CONTENT_OPTIONS } from 'pagesNew/SuccessStory/Create/Form'
import { handleServerErrors, prepareHtmlForSubmit } from 'pagesNew/SuccessStory/Create'
import { useFiles } from 'pagesNew/SuccessStory/Edit/hooks'
import { DELETE_STRATEGY, FIND_ALL_STRATEGY, FIND_ONE_STRATEGY, UPDATE_STRATEGY } from 'queries/strategies'
import { findOneStrategy, findOneStrategyVariables } from '__generatedTypes__/findOneStrategy'
import { updateStrategy, updateStrategyVariables } from '__generatedTypes__/updateStrategy'
import { removeStrategy, removeStrategyVariables } from '__generatedTypes__/removeStrategy'
import { QUERY_PARAM as SUBJECT_QUERY_PARAM } from 'pagesNew/Strategies/List'
import { toast } from 'utils/toast'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { findAllStrategy, findAllStrategyVariables } from '__generatedTypes__/findAllStrategy'
import { CARD_GROUP_QUERY_PARAM } from '../List/components/GroupCards'

type Props = {
  platform: Platform
}

export const Edit = withPlatformCheck(({ platform }: Props) => {
  const { patStrategyId } = useParams()
  const [searchParams] = useSearchParams()

  const sectionId = searchParams.get('sectionId')

  const uploadImage = useImageUpload()

  const { navigate } = useLink()

  const { fetchFromLinks, coverFile, isLoading: isLoadingImages } = useFiles()

  const { data, loading } = useQuery<findOneStrategy, findOneStrategyVariables>(FIND_ONE_STRATEGY, {
    variables: {
      id: patStrategyId || '',
    },
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      const strategy = data.findOneStrategy

      const coverLink = strategy?.getCoverImage || ''
      const initialCoverLink = strategy?.coverImage

      await fetchFromLinks(
        {
          imageLink: '',
        },
        {
          imageLink: coverLink,
          initialLink: initialCoverLink,
        }
      )
    },
    skip: !patStrategyId,
  })

  const [getStrategies] = useLazyQuery<findAllStrategy, findAllStrategyVariables>(FIND_ALL_STRATEGY)

  const [updateStrategy] = useMutation<updateStrategy, updateStrategyVariables>(UPDATE_STRATEGY)
  const [removeStrategy, { loading: isDeleting }] = useMutation<removeStrategy, removeStrategyVariables>(
    DELETE_STRATEGY
  )

  const initialValues: FormValues | null = useMemo(() => {
    const strategy = data?.findOneStrategy

    if (!strategy || isLoadingImages) {
      return null
    }

    return {
      author: [strategy.author],
      title: strategy.title,
      contentAsHtml: strategy.content,
      withWhiteImageBG: strategy.withWhiteImageBG,
      featuredArticleDescription: strategy.featuredArticleDescription,
      description: strategy.description,
      slug: strategy.slug,
      coverImage: coverFile && [coverFile],
      tableOfContent: strategy.tableOfContent,
      order: strategy.order,
      strategyGroup: {
        id: strategy.strategyGroup.id,
        title: strategy.strategyGroup.title,
      },
      membershipLevel: {
        id: strategy.membershipLevel,
        title: strategy.membershipLevel,
      },
    }
  }, [loading, isLoadingImages])

  const uploadFile = async (coverImage: ImageField | undefined) => {
    const coverImageFile = coverImage?.isUploaded ? null : coverImage?.file

    const coverImagePath = await (coverImageFile
      ? uploadImage(coverImageFile, BLOG_POST_MEDIA_PATH_S3)
      : Promise.resolve(coverImage?.initialValue))

    return coverImagePath
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const {
        title,
        description = '',
        featuredArticleDescription,
        slug,
        withWhiteImageBG = false,
        membershipLevel,
        strategyGroup,
        order: orderValue,
      } = values
      const tableOfContent = getTableOfContent(values.contentAsHtml)
      const { error } = validateTableOfContent(tableOfContent, VALIDATE_TABLE_OF_CONTENT_OPTIONS)

      if (error) {
        return
      }

      const htmlContent = await prepareHtmlForSubmit(values)

      const coverImage = await uploadFile(values.coverImage?.[0])

      const authorId = values.author[0].id || ''

      const getLastOrder = async () => {
        if (strategyGroup?.id && initialValues?.strategyGroup?.id !== strategyGroup?.id) {
          const { data: strategyCardsResponse } = await getStrategies({
            variables: {
              query: {
                take: TAKE_ALL_ITEMS_COUNT,
                where: {
                  strategyGroup: {
                    id: strategyGroup.id,
                  },
                },
              },
            },
            fetchPolicy: 'cache-and-network',
          })

          const strategies = strategyCardsResponse?.findAllStrategy.items || []
          const lastOrder = last(strategies)?.order
          return lastOrder ? lastOrder + 1 : orderValue
        }
      }

      const order = await getLastOrder()

      await updateStrategy({
        variables: {
          updateStrategyInputFieldName: {
            id: patStrategyId,
            title,
            description: description ?? '',
            featuredArticleDescription,
            slug,
            author: {
              id: authorId,
            },
            order,
            content: htmlContent,
            withWhiteImageBG,
            strategyGroup: {
              id: String(strategyGroup?.id),
            },
            coverImage,
            tableOfContent,
            membershipLevel: membershipLevel?.id,
          },
        },
      })

      if (sectionId) {
        navigate(cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES, {
          id: sectionId,
        })

        return
      }

      navigate(
        cmsRoutes.STRATEGIES,
        {},
        {
          searchParams: createSearchParams({
            [SUBJECT_QUERY_PARAM]: String(searchParams.get(SUBJECT_QUERY_PARAM)),
            [CARD_GROUP_QUERY_PARAM]: String(searchParams.get(CARD_GROUP_QUERY_PARAM)),
          }),
        }
      )
      toast.success('Strategy updated successfully')
    } catch (error) {
      toast.error('Failed to update strategy')

      return handleServerErrors(error)
    }
  }

  const handleDelete = async () => {
    try {
      await removeStrategy({
        variables: {
          removeStrategyId: patStrategyId || '',
        },
      })

      toast.success('Strategy deleted successfully')

      if (sectionId) {
        navigate(cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES, {
          id: sectionId,
        })

        return
      }

      navigate(
        cmsRoutes.STRATEGIES,
        {},
        {
          searchParams: createSearchParams({
            [SUBJECT_QUERY_PARAM]: String(searchParams.get(SUBJECT_QUERY_PARAM)),
            [CARD_GROUP_QUERY_PARAM]: String(searchParams.get(CARD_GROUP_QUERY_PARAM)),
          }),
        }
      )
    } catch (error) {
      toast.error('Failed to delete strategy')
    }
  }

  if (loading || isLoadingImages) {
    return <PageLoader />
  }

  if (!initialValues) {
    return <Typography>Something went wrong</Typography>
  }

  return (
    <section css={classes.container}>
      <Form
        isEditing
        initialValues={initialValues}
        blogPostType={BlogPostType.PatStrategy}
        platform={platform}
        entityId={patStrategyId}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </section>
  )
})
